/**
 * @fileoverview Notifications data + `useNotifications` hook.
 *
 * IronCase-domain notification fixtures (warrants, evidence pipelines,
 * surveillance gaps, HITL approvals, RMS imports) modelled on Foundry's
 * `notification-components` rows: an intent-colored status chip + icon,
 * a body (optionally with resource links), an optional primary action, and a
 * relative timestamp. No backend exists yet, so these are static fixtures —
 * the same mock-fallback convention used by `useCases` (see `lib/hooks.ts`).
 *
 * "Unacknowledged" state is tracked client-side: we persist the id of the
 * newest notification the user has seen to localStorage. Anything newer than
 * that (i.e. above it in the newest-first list) counts as unread and lights
 * the rail dot. Opening the popover acknowledges up to the newest item.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router";

import type { IconName, Intent } from "@blueprintjs/core";

export interface Notification {
  id: string;
  /** Drives the status-chip color. "none" renders the neutral grey chip. */
  intent: Intent;
  icon: IconName;
  /** Optional bold lead line (Foundry's `*-notification-title`). */
  title?: string;
  /** Descriptive body — may interleave text with resource links. */
  body: ReactNode;
  /** Optional primary action rendered as a small button. */
  action?: { text: string; href: string };
  /** Pre-formatted relative time (kept static for deterministic SSR). */
  time: string;
}

/** A bold inline resource reference, matching Foundry's resource links. */
function Ref({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="bp-notification-ref">
      <strong>{children}</strong>
    </Link>
  );
}

const ROBBERY = "/workspace/compass/view/case-2026-00945";
const BURGLARY = "/workspace/compass/view/case-2026-00821";

/** Newest-first. Adding a new item at the top makes it "unread" for everyone
   who had already acknowledged the previous newest item. */
export const NOTIFICATIONS: readonly Notification[] = [
  {
    id: "ntf-warrant-approved",
    intent: "success",
    icon: "endorsed",
    title: "Search warrant approved",
    body: (
      <>
        Judge M. Vega approved the search warrant for{" "}
        <Ref to={ROBBERY}>DR 26-00945 — Armed robbery</Ref>.
      </>
    ),
    action: { text: "View warrant", href: `${ROBBERY}/warrants` },
    time: "32 minutes ago",
  },
  {
    id: "ntf-ocr-complete",
    intent: "success",
    icon: "build",
    body: (
      <>
        OCR & transcription completed for 12 files in{" "}
        <Ref to={`${ROBBERY}/evidence`}>Armed robbery — Tropicana & Mountain Vista</Ref>.
      </>
    ),
    time: "2 hours ago",
  },
  {
    id: "ntf-surveillance-gap",
    intent: "danger",
    icon: "issue",
    title: "Surveillance gap detected",
    body: (
      <>
        Gap detection flagged a 3h 12m coverage gap in <Ref to={ROBBERY}>DR 26-00945</Ref>.
      </>
    ),
    action: { text: "Review gap", href: `${ROBBERY}/surveillance` },
    time: "Yesterday at 9:14 PM",
  },
  {
    id: "ntf-poi-match",
    intent: "warning",
    icon: "predictive-analysis",
    body: (
      <>
        Facial-recognition match (87%) on a person of interest linked to{" "}
        <Ref to={`${ROBBERY}/entities`}>DR 26-00945</Ref>.
      </>
    ),
    action: { text: "Review match", href: `${ROBBERY}/entities` },
    time: "Yesterday at 2:03 PM",
  },
  {
    id: "ntf-approval-pending",
    intent: "warning",
    icon: "inbox",
    title: "Approval requested",
    body: (
      <>
        An arrest recommendation in <Ref to={BURGLARY}>DR 26-00821 — Westside auto-parts</Ref> is
        awaiting your approval.
      </>
    ),
    action: { text: "Open approval", href: `${BURGLARY}/approvals` },
    time: "2 days ago",
  },
  {
    id: "ntf-case-assigned",
    intent: "none",
    icon: "folder-open",
    body: (
      <>
        Det. Reyes assigned you to{" "}
        <Ref to={BURGLARY}>Burglary investigation — Westside auto-parts</Ref>.
      </>
    ),
    time: "2 days ago",
  },
  {
    id: "ntf-import-failed",
    intent: "warning",
    icon: "import",
    body: (
      <>
        RMS import of <strong>booking-records</strong> failed — 3 records rejected.
      </>
    ),
    time: "3 days ago",
  },
  {
    id: "ntf-entities-extracted",
    intent: "success",
    icon: "diagram-tree",
    body: (
      <>
        Entity extraction completed for <Ref to={`${ROBBERY}/entities`}>DR 26-00945</Ref> — 47
        entities, 23 relationships.
      </>
    ),
    time: "3 days ago",
  },
  {
    id: "ntf-pii-approved",
    intent: "success",
    icon: "tick",
    title: "PII access approved",
    body: (
      <>
        Your request to view encrypted PII in <Ref to={ROBBERY}>DR 26-00945</Ref> was approved.
      </>
    ),
    action: { text: "View details", href: ROBBERY },
    time: "4 days ago",
  },
];

const ACK_KEY = "ic.notifications.ackId";

export interface UseNotifications {
  notifications: readonly Notification[];
  unreadCount: number;
  unreadIds: ReadonlySet<string>;
  /** Mark everything up to the newest item as seen (clears the rail dot). */
  acknowledge: () => void;
}

export function useNotifications(): UseNotifications {
  const notifications = NOTIFICATIONS;
  const [ackId, setAckId] = useState<string | null>(null);

  // SSR-safe: server render treats all as unread (matches a fresh visitor);
  // hydrate the acknowledged id from localStorage on mount.
  useEffect(() => {
    try {
      setAckId(window.localStorage.getItem(ACK_KEY));
    } catch {
      // localStorage unavailable — keep everything unread
    }
  }, []);

  const unreadIds = useMemo(() => {
    const ackIndex = ackId ? notifications.findIndex((n) => n.id === ackId) : -1;
    // ackIndex === -1 → never acknowledged (or ack id no longer exists): all
    // unread. Otherwise everything above the acknowledged item is unread.
    const cutoff = ackIndex === -1 ? notifications.length : ackIndex;
    return new Set(notifications.slice(0, cutoff).map((n) => n.id));
  }, [ackId, notifications]);

  const acknowledge = useCallback(() => {
    const newest = notifications[0]?.id;
    if (!newest) return;
    try {
      window.localStorage.setItem(ACK_KEY, newest);
    } catch {
      // localStorage unavailable — state still updates in memory
    }
    setAckId(newest);
  }, [notifications]);

  return { notifications, unreadCount: unreadIds.size, unreadIds, acknowledge };
}
